import { Chart } from "primereact/chart";
import { getFontFamily } from "../services/util.service";
import { useTheme } from "../context/ThemeContext";
import { useEffect, useMemo, useState } from "react";

export default function BarChart(
    {
        id,
        data,
        className,
        xText,
        yText,
        stacked,
        barThickness,
        isScroll
    }: {
        id: string,
        data?: any,
        className?: string,
        xText?: string,
        yText?: string,
        stacked?: boolean,
        barThickness?: boolean,
        isScroll?: boolean
    }
) {

    const { theme } = useTheme()

    const textColor = theme === 'light' ? '#374151' : '#f3f4f6';
    const gridColor = theme === 'light' ? '#0000001A' : '#374151';

    const [fontSize, setFontSize] = useState(
        Number(localStorage.getItem("appFontSize")) || 14
    );

    useEffect(() => {
        const handleFontSizeChange = () => {
            setFontSize(Number(localStorage.getItem("appFontSize")) || 14);
        };

        window.addEventListener("fontSizeChanged", handleFontSizeChange);

        return () => {
            window.removeEventListener("fontSizeChanged", handleFontSizeChange);
        };
    }, []);

    const [fontFamily, setFontFamily] = useState(getFontFamily());

    useEffect(() => {
        const handleFontFamilyChange = () => {
            setFontFamily(getFontFamily());
        };

        window.addEventListener("fontFamilyChanged", handleFontFamilyChange);

        return () => {
            window.removeEventListener("fontFamilyChanged", handleFontFamilyChange);
        };
    }, []);

    const options = useMemo(
        () => ({
            ...(barThickness && {
                datasets: {
                    bar: {
                        barThickness: 30,
                    }
                }
            }),
            interaction: {
                mode: 'index',
                intersect: false
            },
            plugins: {
                legend: {
                    position: "bottom",
                    onClick: () => { },
                    labels: {
                        boxWidth: 8,
                        boxHeight: 8,
                        padding: 12,
                        color: textColor,
                        font: {
                            size: fontSize,
                            family: fontFamily
                        },
                    }
                },
                tooltip: {
                    titleFont: {
                        size: fontSize,
                        family: fontFamily
                    },
                    bodyFont: {
                        size: fontSize,
                        family: fontFamily
                    },
                    footerFont: {
                        size: fontSize,
                        family: fontFamily
                    },
                    filter: function (context: any) {
                        const chart = context.chart;
                        const dataIndex = context.dataIndex;

                        // Check if any dataset has a non-zero value at this index
                        const hasNonZeroValue = chart.data.datasets.some((dataset: any) => {
                            const value = dataset.data?.[dataIndex];
                            return Number(value) !== 0;
                        });

                        // Show tooltip only if at least one value is non-zero
                        return hasNonZeroValue;
                    },
                    callbacks: {
                        // 1. X-axis label (top)
                        title: function (context: any) {
                            return context[0]?.label; // timeBucket
                        },

                        // 2. Date range (next line)
                        afterTitle: (context: any) => {
                            if (id === "tag-miss-catergory") {
                                const stationName =
                                    context[0]?.dataset?.stationName?.[context[0].dataIndex] ?? "-";

                                const stationId =
                                    context[0]?.dataset?.stationId?.[context[0].dataIndex] ?? "-";

                                return `${stationName} (${stationId})`;
                            }
                            return context[0]?.chart?.data?.dateRange?.[context[0].dataIndex] || '';
                        },

                        // 3. Count (per dataset line)
                        label: function (context: any) {
                            const label = context.dataset.label;
                            const value = context.parsed.y ?? 0;

                            if (value == null || Number(value) === 0 || isNaN(Number(value))) {
                                return null;
                            }

                            return `${label}: ${value}`;
                        }
                    }
                },
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    stacked: stacked ?? false,
                    ticks: {
                        color: textColor,
                        font: { size: fontSize, family: fontFamily }
                    },
                    border: { display: true, color: gridColor },
                    grid: { color: gridColor },
                    ...(xText && {
                        title: {
                            display: true, color: textColor, text: xText, font: {
                                size: fontSize,
                                family: fontFamily,
                            }
                        }
                    })
                },
                y: {
                    stacked: stacked ?? false,
                    ticks: {
                        color: textColor,
                        font: { size: fontSize, family: fontFamily }
                    },
                    border: { display: true, color: gridColor },
                    grid: { color: gridColor },
                    ...(yText && {
                        title: {
                            display: true, color: textColor, text: yText, font: {
                                size: fontSize,
                                family: fontFamily,
                            }
                        }
                    })
                }
            }
        }), [fontSize, theme, , xText, yText, id, data, fontFamily]);

    // 👇 dynamic width based on data points
    const chartWidth = isScroll
        ? Math.max((data?.labels?.length || 0) * 80, 600)
        : "100%";

    return (
        <div style={{ overflowX: isScroll ? "auto" : "hidden" }}>
            <div style={{ width: chartWidth }}>
                <Chart
                    id={id}
                    type="bar"
                    data={data}
                    options={options}
                    className={`${className ?? ""}`}
                />
            </div>
        </div>
    );
}
